import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateDiscountRulesetMutation } from "store/queries/procedureDiscountRuleset";

import {
  formApplicability,
  getRulesetList,
  mapFormToRequestBody,
} from "../helpers/mapFormToRequestBody";
import { useRuleScopeComplete } from "./RuleApplicabilityField";

export function Submit() {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  // CLE-2842 step 2 — a Payer-Provider rule needs its provider and payer, whatever the
  // scope fields' mount timing did to react-hook-form's own validity.
  const scopeComplete = useRuleScopeComplete();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [useCreateDiscountRuleset] = useCreateDiscountRulesetMutation();

  const { current: toastElement } = useContext(ToastContext);

  const createPayerRule = async () => {
    const values = getValues();
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(values.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const rulesToCreateRaw = getRulesetList({ ...values, cptCodes });
    const rulesToCreate = rulesToCreateRaw.map((rule) => {
      // CLE-2524: creates declare applicability explicitly (wins over isDefault) — since
      // CLE-2842 step 2 that includes FALLBACK.
      return {
        ...mapFormToRequestBody(rule),
        applicability: formApplicability(rule),
      };
    });
    setLoading(true);
    try {
      const results = await Promise.all(
        rulesToCreate.map((rule) =>
          useCreateDiscountRuleset(rule)
            .unwrap()
            .catch(() => false)
        )
      );
      const allSuccessful = results.every((result) => result !== false);
      if (allSuccessful) {
        toastElement?.show({
          severity: "success",
          summary: "Payer Ruleset(s) created",
          detail: "Payer Ruleset(s) created successfully",
        });

        navigate("/payer-rules");
      } else {
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail:
            "An error has occurred attempting to create one or more discount rulesets",
        });
      }
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "An error has occurred",
      });
    }
    setLoading(false);
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Create Payer Rule"
          onClick={createPayerRule}
          disabled={!isValid || !scopeComplete}
          loading={loading}
        />
      </div>
    </ItemBar>
  );
}
